import React from 'react';
import { Button, TextField, Switch, FormControlLabel, Divider } from '@mui/material';
import {
  Container,
  SectionTitle,
  SettingsSection,
  SectionHeader,
  SettingsGrid,
  SettingItem,
  SettingLabel,
  SettingDescription,
  ActionButtons,
} from './Settings.styles';
import { useTheme, alpha } from '@mui/material/styles';

export default function Settings() {
  const theme = useTheme();

  return (
    <Container>
      <SectionTitle>Settings</SectionTitle>
      <p>Configure application preferences and account settings.</p>
      
      <SettingsSection>
        <SectionHeader>General Settings</SectionHeader>
        
        <SettingsGrid>
          <SettingItem>
            <SettingLabel>Default Document Format</SettingLabel>
            <TextField
              select
              fullWidth
              size="small"
              variant="outlined"
              SelectProps={{
                native: true,
              }}
              defaultValue="docx"
            >
              <option value="docx">Microsoft Word (.docx)</option>
              <option value="pdf">PDF Document (.pdf)</option>
              <option value="txt">Plain Text (.txt)</option>
              <option value="rtf">Rich Text Format (.rtf)</option>
            </TextField>
            <SettingDescription>
              Select the default format for generated documents
            </SettingDescription>
          </SettingItem>
          
          <SettingItem>
            <SettingLabel>Default Citation Style</SettingLabel>
            <TextField
              select
              fullWidth
              size="small"
              variant="outlined"
              SelectProps={{
                native: true,
              }}
              defaultValue="bluebook"
            >
              <option value="bluebook">Bluebook</option>
              <option value="apa">APA</option>
              <option value="chicago">Chicago</option>
              <option value="mla">MLA</option>
            </TextField>
            <SettingDescription>
              Choose the preferred citation style for legal documents
            </SettingDescription>
          </SettingItem>
        </SettingsGrid>
        
        <div style={{ marginTop: '15px' }}>
          <FormControlLabel
            control={<Switch defaultChecked color="primary" />}
            label="Enable document auto-save"
          />
          <SettingDescription>
            Automatically save document changes every 2 minutes
          </SettingDescription>
        </div>
        
        <div style={{ marginTop: '15px' }}>
          <FormControlLabel
            control={<Switch defaultChecked color="primary" />}
            label="Show document version history"
          />
          <SettingDescription>
            Display version history when editing documents
          </SettingDescription>
        </div>
      </SettingsSection>
      
      <SettingsSection>
        <SectionHeader>AI Assistant Settings</SectionHeader>
        
        <SettingsGrid>
          <SettingItem>
            <SettingLabel>AI Model</SettingLabel>
            <TextField
              select
              fullWidth
              size="small"
              variant="outlined"
              SelectProps={{
                native: true,
              }}
              defaultValue="gpt4"
            >
              <option value="gpt4">GPT-4 (Premium)</option>
              <option value="gpt35">GPT-3.5</option>
              <option value="llama2">Llama 2</option>
              <option value="claude">Claude</option>
            </TextField>
            <SettingDescription>
              Select the AI model for document analysis and generation
            </SettingDescription>
          </SettingItem>
          
          <SettingItem>
            <SettingLabel>Research Depth</SettingLabel>
            <TextField
              select
              fullWidth
              size="small"
              variant="outlined"
              SelectProps={{
                native: true,
              }}
              defaultValue="thorough"
            >
              <option value="quick">Quick</option>
              <option value="standard">Standard</option>
              <option value="thorough">Thorough</option>
              <option value="comprehensive">Comprehensive</option>
            </TextField>
            <SettingDescription>
              Set the depth level for AI-powered legal research
            </SettingDescription>
          </SettingItem>
        </SettingsGrid>
        
        <div style={{ marginTop: '15px' }}>
          <FormControlLabel
            control={<Switch defaultChecked color="primary" />}
            label="Enable AI suggestions while drafting"
          />
          <SettingDescription>
            Show AI-powered suggestions for legal language and clauses
          </SettingDescription>
        </div>
      </SettingsSection>
      
      <SettingsSection>
        <SectionHeader>Account Settings</SectionHeader>
        
        <SettingsGrid>
          <SettingItem>
            <SettingLabel>Email Address</SettingLabel>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="your.email@example.com"
              defaultValue="john.smith@legalfirm.com"
            />
          </SettingItem>
          
          <SettingItem>
            <SettingLabel>Password</SettingLabel>
            <TextField
              fullWidth
              size="small"
              type="password"
              variant="outlined"
              defaultValue="********"
            />
            <SettingDescription>
              Last changed 30 days ago
            </SettingDescription>
          </SettingItem>
        </SettingsGrid>
        
        <Divider style={{ margin: '20px 0' }} />
        
        <div>
          <SectionHeader style={{ fontSize: '1.1rem', marginBottom: '10px' }}>
            Subscription Plan
          </SectionHeader>
          <div
            style={{
              padding: '15px',
              backgroundColor: alpha(theme.palette.secondary.main, 0.1),
              border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
              borderRadius: '4px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div>
              <h3 style={{ margin: '0 0 5px 0' }}>Enterprise Plan</h3>
              <p style={{ margin: '0', fontSize: '0.9rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                Unlimited documents, premium AI models, priority support
              </p>
            </div>
            <Button
              variant="outlined"
              sx={{
                borderColor: theme.palette.secondary.main,
                color: theme.palette.secondary.main,
                '&:hover': {
                  borderColor: theme.palette.secondary.dark,
                  color: theme.palette.secondary.dark,
                },
              }}
            >
              Manage Subscription
            </Button>
          </div>
        </div>
      </SettingsSection>
      
      <ActionButtons>
        <Button 
          variant="outlined"
          size="large"
        >
          Cancel
        </Button>
        <Button 
          variant="contained"
          size="large"
          sx={{
            backgroundColor: theme.palette.secondary.main,
            color: '#fff',
            '&:hover': {
              backgroundColor: theme.palette.secondary.light,
            },
          }}
        >
          Save Changes
        </Button>
      </ActionButtons>
    </Container>
  );
}